import { EntityDetailShell } from '@startupquickstart/frontend';
import type { PromotionalOffer } from '@/config/objects/promotional-offers';
import {
  Button,
  ConfirmDialog,
  StatusPill,
  useApiClient,
  useRequireOrganization,
  toast,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Pencil, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const tabs = [
  { label: 'Overview', path: 'overview', icon: Tag },
  { label: 'Hotels', path: 'hotels', icon: Building2 },
] as const;

export function PromotionalOfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { isInternal } = useRequireOrganization();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PromotionalOffer | null>(null);
  const basePath = `/promotional-offers/${id}`;

  const deleteMutation = useMutation({
    mutationFn: async (offerId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/promotional-offers/${offerId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['PromotionalOffer'] });
        navigate('/promotional-offers');
      } else {
        throw new Error(response.error || 'Failed to delete promotional offer');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting promotional offer...',
      success: 'Promotional offer deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete promotional offer',
    });
  };

  return (
    <>
      <EntityDetailShell<PromotionalOffer>
        resource="promotional-offers"
        queryKey="PromotionalOffer"
        id={id}
        basePath={basePath}
        entityLabel="Promotional Offer"
        tabs={tabs}
        contextKey="offer"
        hero={{
          category: { label: 'Promotional Offer', icon: Tag },
          title: (o) => o?.name || 'Promotional Offer',
          imageUrl: (o) => o?.featuredImage?.url ?? null,
          fallbackIcon: Tag,
          imageAlt: (o) => o?.name || 'Promotional Offer',
          meta: (o) => (o.title ? <span>{o.title}</span> : null),
          badges: (o) => (
            <>
              {o.status && <StatusPill status={o.status} />}
              {o.isActive !== undefined && (
                <StatusPill
                  status={o.isActive ? 'published' : 'needs_review'}
                  label={o.isActive ? 'Active' : 'Inactive'}
                />
              )}
            </>
          ),
        }}
        breadcrumbs={(o) => [
          { label: 'Home', href: '/' },
          { label: 'Promotional Offers', href: '/promotional-offers' },
          { label: o?.name || 'Promotional Offer', current: true },
        ]}
        headerActions={(o) =>
          o ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {isInternal && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    setPendingDelete(o);
                    setShowDeleteConfirm(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Promotional Offer"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
